<template>
  <div id="top" class="provider-sync-page">
    <div class="provider-sync-intro">
      <UiPageHeader title="Provider Sync" :description="`Track registrations awaiting oneProvider approval and match approved providers into ${brand.productName}.`">
        <template #actions><router-link :to="{ name: 'provider' }" class="provider-sync-back"><i class="material-icons" aria-hidden="true">arrow_back</i>All providers</router-link></template>
      </UiPageHeader>
    </div>
    <LoadingDiv v-if="!isReady" />
    <div class="provider-sync-workbench sba-slide-in" v-else>
      <section class="provider-sync-body">
        <aside class="provider-sync-list-panel">
          <div class="provider-sync-list-head">
            <span>Waiting oneProvider approval</span>
            <small>{{ pendingRegistrationTotal }} pending</small>
          </div>

          <div class="provider-registration-list" v-if="pendingRegistrations.length">
            <div
              v-for="registration in pendingRegistrations"
              :key="registration.id"
              class="provider-registration-item"
            >
              <strong>{{ registration.submitted_name }}</strong>
              <span>{{ registrationStatusText(registration.status) }}</span>
              <small>{{ registration.submitted_user_email || registration.submitted_email || 'No email recorded' }}</small>
            </div>
          </div>

          <div class="provider-registration-empty" v-else>
            <i class="material-icons">hourglass_empty</i>
            <span>No provider is waiting on oneProvider approval.</span>
          </div>

          <div class="provider-sync-section-divider" aria-hidden="true"></div>

          <div class="provider-sync-list-head">
            <span>Ready for payer matching</span>
            <small>{{ pendingProviderTotal }} pending</small>
          </div>

          <ol class="provider-sync-list" :start="providerListStart">
            <li v-for="provider in providers" :key="provider.id">
              <button type="button" @click="handleSync(provider)">{{ provider.name }}</button>
              <span v-if="provider.pivot.status === 'a'">Done</span>
            </li>
          </ol>

          <div class="provider-sync-empty center" v-if="!providers.length">
            <i class="material-icons">sync_disabled</i>
            <h6>No provider found</h6>
            <p>There are no approved oneProvider records waiting for payer matching.</p>
          </div>

          <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-if="showPagination" />
        </aside>

        <section class="provider-sync-detail-panel">
          <SyncProvider :oneProvider="provider" @clear="handleClear" @update="handleUpdate" v-if="provider" />
          
          <div class="provider-sync-placeholder" :class="{ 'is-loading-provider': isProviderLoading }" v-else>
            <div v-if="!isProviderLoading">
              <i class="material-icons">medical_services</i>
              <h5>Select Provider</h5>
              <p>Click an approved provider from the ready list to match it with the {{ brand.productName }} provider record.</p>
            </div>
            <div v-else>
              <i class="material-icons">sync</i>
              <h5>Selecting Provider for Syncing</h5>
              <p>Please wait while provider details are loading.</p>
            </div>
          </div>

          <!-- isProviderLoading -->
          <div class="progress provider-sync-progress" v-if="isProviderLoading">
            <div class="indeterminate"></div>
          </div>
        </section>
      </section>
    </div>
  </div>
</template>


<script>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import getItem from "@/composables/getItem";
import getItems from "@/composables/getItems";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import SyncProvider from "@/components/provider/SyncProviderComponent.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import brand from "@/whitelabel/brand.config";


export default {
  name: "providerSync",
  components: { UiPageHeader, SyncProvider, LoadingDiv, PaginationDiv },
  setup() {
    const response = ref(null);
    const registrationResponse = ref(null);
    const isReady = ref(false);
    const isLoading = ref(false);
    const isProviderLoading = ref(false);
    const currUrl = ref(null);
    const currHook = ref(null);
    const error = ref(null);
    const provider = ref(null);
    const searchItem = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);

    // vue router
    const router = useRouter();

    // start
    onMounted(() => {
      // page name
      document.title = "Sync Providers";
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Providers", title: "All", icon: "arrow_back", url: "provider", hash: null, });
      tabs.value.push({ id: 2, name: "Providers", title: "View", icon: "visibility", url: "provider", hash: "#view", });
      tabs.value.push({ id: 3, name: "Providers", title: "Add Provider", icon: "add_circle", url: "providerAdd", hash: null, });
      tabs.value.push({ id: 4, name: "Providers", title: "Sync", icon: null, url: null, hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("Sync"));
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    // handle curr status
    const handleActive = (tab) => {
      // refer
      if (tab.hash) {
        router.push({ name: tab.url, hash: tab.hash });
      } else {
        router.push({ name: tab.url });
      }
    };

    // numbering
    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    const getPendingRegistrationsFromApi = () => {
      (async () => {
        const { res, err } = await getItems("integration/oneprovider/provider-sync/registrations/pending");

        if (res.value) {
          registrationResponse.value = res.value.data;
        } else {
          error.value = err.value;
        }
      })();
    };

    // starter
    onMounted(() => {
      // set current Url
      currUrl.value = "integration/oneprovider/provider-sync/pending";
      // set current Hook
      currHook.value = "?";
      // get items from function
      getItemsFromApi(currUrl.value);
      getPendingRegistrationsFromApi();
    });

    // clear
    const handleClear = (val) => {
      if (val) {
       // clear provider
       provider.value = null;
      }
    }

    // clear
    const handleUpdate = (val) => {
      if (val) {
       // update provider
       response.value.data.forEach((x) => {
        if (x.id === val.id) {
          // get all
          x.pivot.status = val;
        }
       });
      }
    }

    const scrollToPageTop = () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // sync
    const handleSync = (val) => {
      scrollToPageTop();

      // start
      provider.value = null;
      // start loading
      isProviderLoading.value = true;

      // confirm availabity again
      (async () => {
        const { res, err } = await getItem("integration/oneprovider/provider-sync/providers", val.opid);

        if (res.value) {
          provider.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // end
        isProviderLoading.value = false;

      })();
    }

    // switch page
    const handlePage = (page) => {
      if (Number(page) === Number(allPages.value.current_page)) {
        return;
      }

      window.scrollTo({ top: 0, behavior: "smooth" });
      // start laoding
      isLoading.value = true;
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`); //
    };

    // output results
    const providers = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    const pendingRegistrations = computed(() => {
      if (!registrationResponse.value) {
        return [];
      } else {
        return registrationResponse.value.data || [];
      }
    });

    const pendingProviderTotal = computed(() => {
      if (!response.value) {
        return 0;
      } else {
        return response.value.total ?? providers.value.length;
      }
    });

    const pendingRegistrationTotal = computed(() => {
      if (!registrationResponse.value) {
        return 0;
      } else {
        return registrationResponse.value.total ?? pendingRegistrations.value.length;
      }
    });

    const registrationStatusText = (status) => {
      switch (status) {
        case "needs_duplicate_review":
          return "Duplicate review";
        case "pending_super_admin_review":
          return "Identity review";
        default:
          return "Pending approval";
      }
    };

    const providerListStart = computed(() => {
      if (!response.value) {
        return 1;
      } else {
        return ((response.value.current_page ?? 1) - 1) * (response.value.per_page ?? providers.value.length) + 1;
      }
    });

    const showPagination = computed(() => {
      if (!response.value) {
        return false;
      } else {
        return (response.value.total ?? 0) > (response.value.per_page ?? providers.value.length);
      }
    });

    return {
      brand,
      handleClear,
      handleUpdate,
      handleActive,
      home,
      currTab,
      tabs,
      numberBadge,
      handlePage,
      handleSync,
      isReady,
      isLoading,
      isProviderLoading,
      error,
      provider,
      searchItem,
      providers,
      pendingRegistrations,
      allPages,
      pendingProviderTotal,
      pendingRegistrationTotal,
      registrationStatusText,
      providerListStart,
      showPagination,
    };
  },
};
</script>

<style scoped>
.provider-sync-page { box-sizing: border-box; margin: 0 auto; max-width: var(--ui-content-width); padding: 2rem 2rem 3rem; width: 100%; }
.provider-sync-intro { border-bottom: 1px solid var(--ui-border); margin-bottom: 2.5rem; padding-bottom: 1.75rem; }
.provider-sync-intro :deep(.ui-page-header) { margin-bottom: 0; }
.provider-sync-intro :deep(.ui-page-header__title) { font-size: clamp(1.75rem, 2.1vw, 2rem); font-weight: 700; letter-spacing: -.025em; }
.provider-sync-intro :deep(.ui-page-header__description) { font-size: 1rem; line-height: 1.55; }
.provider-sync-back { align-items: center; background: var(--ui-primary); border: 1px solid var(--ui-primary); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-primary-foreground); display: inline-flex; font-size: .8125rem; font-weight: 600; gap: .5rem; min-height: 2.5rem; padding: 0 .9rem; text-decoration: none; }
.provider-sync-back i { font-size: 1rem; }
.provider-sync-page .provider-sync-list button { background: transparent; border: 0; border-radius: calc(var(--ui-radius) - 3px); color: var(--ui-foreground); cursor: pointer; font-family: var(--ui-font-sans); font-size: .8125rem; font-weight: 500; line-height: 1.5; padding: .6rem .75rem; text-align: left; width: 100%; }
.provider-sync-page .provider-sync-list button:hover { background: var(--ui-muted); }
.provider-sync-page :is(a, button):focus-visible { outline: 2px solid var(--ui-ring); outline-offset: 2px; }
@layer components {
  .provider-sync-page .provider-sync-body { background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; gap: 3rem !important; grid-template-columns: 18rem minmax(0, 1fr) !important; padding: 0 !important; }
  .provider-sync-page :is(.provider-sync-list-panel, .provider-sync-detail-panel) { background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; padding: 0 !important; }
  .provider-sync-page .provider-sync-list-head { gap: .75rem; }
  .provider-sync-page .provider-sync-list-head span { color: var(--ui-foreground) !important; font-size: .8125rem !important; font-weight: 600 !important; text-transform: none !important; }
  .provider-sync-page .provider-sync-list-head small { font-size: .6875rem !important; font-weight: 500 !important; white-space: nowrap; }
  .provider-sync-page .provider-sync-list { list-style: none !important; margin: .5rem 0 0 !important; padding: 0 !important; }
  .provider-sync-page .provider-sync-list li { border-bottom: 1px solid var(--ui-border) !important; padding: .25rem 0 !important; }
  .provider-sync-page .provider-registration-item { background: transparent !important; border: 0 !important; border-bottom: 1px solid var(--ui-border) !important; border-radius: 0 !important; padding: .75rem 0 !important; }
  .provider-sync-page .provider-registration-item strong { font-size: .8125rem !important; font-weight: 600 !important; }
  .provider-sync-page .provider-registration-item span { font-size: .6875rem !important; font-weight: 500 !important; text-transform: none !important; }
  .provider-sync-page .provider-registration-item small { font-size: .75rem !important; font-weight: 400 !important; }
  .provider-sync-page .provider-registration-empty { border: 0 !important; font-size: .8125rem; line-height: 1.5; padding: .75rem 0 !important; }
  .provider-sync-page .provider-sync-placeholder { background: var(--ui-background) !important; border: 1px solid var(--ui-border) !important; border-radius: var(--ui-radius) !important; box-shadow: none !important; min-height: 16rem !important; padding: 2rem !important; }
  .provider-sync-page .provider-sync-placeholder h5 { font-size: 1.125rem !important; font-weight: 600 !important; }
  .provider-sync-page .provider-sync-placeholder p { color: var(--ui-muted-foreground); font-size: .875rem !important; line-height: 1.6; }
  .provider-sync-page :deep(.provider-sync-detail-panel .card) { background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; margin: 0 !important; }
  .provider-sync-page :deep(.provider-sync-detail-panel .card-content) { padding: 0 0 1rem !important; }
  .provider-sync-page :deep(.provider-sync-detail-panel .card-title) { font-size: 1.125rem !important; font-weight: 600 !important; }
  .provider-sync-page :deep(.provider-sync-detail-panel .card-panel) { background: transparent !important; border: 0 !important; border-bottom: 1px solid var(--ui-border) !important; border-radius: 0 !important; box-shadow: none !important; margin: 0 !important; padding: 1rem 0 !important; }
  .provider-sync-page :deep(.provider-sync-detail-panel .row) { margin: 0 !important; }
  .provider-sync-page :deep(.provider-sync-detail-panel .input-field) { display: flex !important; flex-direction: column !important; margin: .75rem 0 0 !important; }
  .provider-sync-page :deep(.provider-sync-detail-panel .input-field > label) { font-size: .8125rem !important; font-weight: 500 !important; margin: 0 0 .5rem !important; order: -1; position: static !important; transform: none !important; }
  .provider-sync-page :deep(.provider-sync-detail-panel label:has(input[type="radio"])), .provider-sync-page :deep(.provider-sync-detail-panel label:has(input[type="checkbox"])) { color: var(--ui-foreground); display: block; font-size: .8125rem; line-height: 1.6; padding: .5rem 0; }
  .provider-sync-page :deep(.provider-sync-detail-panel a) { text-decoration: none !important; }
  @media (max-width: 1000px) { .provider-sync-page .provider-sync-body { gap: 1.5rem !important; grid-template-columns: minmax(0, 1fr) !important; } }
}
@media (max-width: 700px) { .provider-sync-page { padding: 1.25rem 1rem 2rem; } }
.provider-sync-workbench {
  color: #263238;
}

.provider-sync-head {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  padding: 24px 26px;
  position: relative;
}

.provider-sync-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.provider-sync-title-block {
  position: relative;
  z-index: 2;
}

.provider-sync-title-block h5,
.provider-sync-placeholder h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.provider-sync-title-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
  max-width: 780px;
}

.provider-sync-body {
  background: #f7f9f9;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  padding: 18px;
}

.provider-sync-list-panel,
.provider-sync-detail-panel {
  background: #ffffff;
  border: 1px solid #d7e1e5;
  border-radius: 6px;
  min-width: 0;
}

.provider-sync-list-panel {
  padding: 16px;
}

.provider-sync-section-divider {
  border-top: 1px solid #e1e8eb;
  margin: 16px 0;
}

.provider-sync-list-head {
  align-items: baseline;
  border-bottom: 1px solid #e1e8eb;
  display: flex;
  justify-content: space-between;
  padding-bottom: 12px;
}

.provider-sync-list-head span {
  color: #263238;
  font-size: 0.9rem;
  font-weight: 800;
  text-transform: uppercase;
}

.provider-sync-list-head small {
  color: #607d8b;
  font-weight: 700;
}

.provider-sync-list {
  margin: 12px 0 0 20px;
}

.provider-sync-list li {
  border-bottom: 1px solid #eef3f5;
  color: #607d8b;
  padding: 9px 0;
}

.provider-sync-list li:last-child {
  border-bottom: 0;
}

.provider-sync-list a {
  color: #00695c;
  font-weight: 700;
}

.provider-sync-list span {
  background: #e8f5e9;
  border-radius: 999px;
  color: #2e7d32;
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 800;
  margin-left: 6px;
  padding: 2px 8px;
  text-transform: uppercase;
}

.provider-registration-list {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}

.provider-registration-item {
  border: 1px solid #e6ecef;
  border-left: 3px solid #f9a825;
  border-radius: 4px;
  background: #fffdf7;
  padding: 10px 11px;
}

.provider-registration-item strong,
.provider-registration-item span,
.provider-registration-item small {
  display: block;
}

.provider-registration-item strong {
  color: #263238;
  font-size: 0.95rem;
  font-weight: 800;
}

.provider-registration-item span {
  color: #8a5a00;
  font-size: 0.78rem;
  font-weight: 900;
  margin-top: 3px;
  text-transform: uppercase;
}

.provider-registration-item small {
  color: #607d8b;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.35;
  margin-top: 5px;
  overflow-wrap: anywhere;
}

.provider-registration-empty {
  align-items: center;
  border: 1px dashed #d7e1e5;
  border-radius: 4px;
  color: #607d8b;
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding: 12px;
}

.provider-registration-empty i {
  color: #90a4ae;
  font-size: 20px;
}

.provider-registration-empty span {
  font-size: 0.86rem;
  font-weight: 650;
  line-height: 1.35;
}

.provider-sync-detail-panel {
  padding: 18px;
  position: relative;
}

.provider-sync-placeholder {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  color: #607d8b;
  display: flex;
  justify-content: center;
  min-height: 280px;
  padding: 24px;
  text-align: center;
}

.provider-sync-placeholder i {
  color: #00897b;
  font-size: 32px;
}

.provider-sync-placeholder p,
.provider-sync-empty p {
  color: #607d8b;
  line-height: 1.5;
  margin: 8px auto 0;
  max-width: 460px;
}

.provider-sync-empty {
  color: #607d8b;
  padding: 28px 10px 10px;
}

.provider-sync-empty i {
  color: #90a4ae;
  font-size: 28px;
}

.provider-sync-placeholder.is-loading-provider {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.provider-sync-progress {
  background-color: #edf4f5;
  border-radius: 0 0 6px 6px;
  height: 4px;
  margin: 0;
}

.provider-sync-progress .indeterminate {
  background-color: #00897b;
}

.provider-sync-detail-panel :deep(.card),
.provider-sync-detail-panel :deep(.card-panel) {
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  box-shadow: none;
}

@media only screen and (max-width: 992px) {
  .provider-sync-body {
    grid-template-columns: 1fr;
  }

  .provider-sync-detail-panel {
    order: 1;
  }

  .provider-sync-list-panel {
    order: 2;
  }
}

@media only screen and (max-width: 600px) {
  .provider-sync-head,
  .provider-sync-body,
  .provider-sync-detail-panel {
    padding: 16px;
  }

  .provider-sync-title-block h5,
  .provider-sync-placeholder h5 {
    font-size: 1.45rem;
  }
}
</style>
